<template>
  <div class="card-membre-expanded" v-if="membre">
    <div class="main-card">
      <div class="about-membre-text-wrapper">
        <div> <!-- Added a wrapper for top content -->
          <div class="card-logo">
            <img 
              v-if="logoInfo.imageUrl && !logoImageError"
              class="aplv-out"
              :alt="memberName + ' Logo'"
              :src="logoInfo.imageUrl"
              decoding="async"
              @error="handleLogoError"
            />
            <!-- The code correctly falls back to showing the member's name. -->
            <!-- No changes are needed here. -->
          </div>
          <p class="card-title" :class="{ 'with-logo': logoInfo.imageUrl && !logoImageError }">
            {{ displayName }}
          </p>
          <div class="categories-wrapper">
            <chips-category
              v-for="categoryName in matchedCategories"
              :key="categoryName"
              :text="categoryName"
              property1="variant-2"
              class-name="chips-category-instance"
            />
          </div>
        </div>
        <p class="card-description" :class="{ 'placeholder-text': memberDescription === 'Déscription non disponible' }">
          {{ memberDescription }}
        </p>
      </div>

      <div class="contact-gallery-wrapper">
        <div class="contact">
          <div 
            class="contact-item clickable-contact" 
            v-if="phoneValue"
            @click.stop="callPhone(phoneValue)"
            :title="'Appeler ' + phoneValue"
          >
            <img
              class="img"
              alt="Ic round call"
              src="/img/ic-round-call.svg"
            />
            <p class="contact-text">{{ phoneValue }}</p>
          </div>

          <div class="contact-item" v-if="addressValue">
            <img
              class="img"
              alt="Material symbols"
              src="/img/material-symbols-home-rounded.svg"
            />
            <p class="contact-text">{{ addressValue }}</p>
          </div>

          <div 
            class="contact-item clickable-contact" 
            v-if="emailValue"
            @click.stop="sendEmail(emailValue)"
            :title="'Envoyer un email à ' + emailValue"
          >
            <img
              class="img"
              alt="Ic round mail"
              src="/img/ic-round-mail.svg"
            />
            <div class="contact-text">{{ emailValue }}</div>
          </div>

          <div 
            class="contact-item clickable-contact" 
            v-if="siteValue"
            @click.stop="openWebsite(siteValue)"
            :title="'Visiter ' + siteValue"
          >
            <img
              class="img"
              alt="Mdi internet"
              src="/img/mdi-internet.svg"
            />
            <div class="contact-text">{{ siteValue }}</div>
          </div>
        </div>

        <div class="card-gallery">
          <template v-if="galleryImages.length > 0">
            <img
              class="gallery-img"
              alt="Product image"
              v-for="(imageSrc, index) in galleryImages"
              :key="index"
              :src="imageSrc"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              @error="handleGalleryImageError(index)"
              @click="showcaseImage(imageSrc)"
            />
          </template>
          <p v-else class="no-images-text">Aucune image disponible</p>
        </div>
      </div>
      <br/>
      
    </div>

    <div class="autres-membres" ref="autresMembres">
      <div class="autres-membres-header" v-if="isMobile">
        <button class="arrow-btn" @click="prevAutresMembresPage" aria-label="Previous members">
          <img class="img" alt="Previous members" src="/img/ep-arrow-right-bold.svg" />
        </button>
        <span class="autres-title">Autres membres</span>
        <button class="arrow-btn" @click="nextAutresMembresPage" aria-label="Next members">
          <img class="img" alt="Next members" src="/img/ep-arrow-right-bold-1.svg" />
        </button>
      </div>
      <div class="handle-top" v-if="!isMobile" @click="prevAutresMembresPage" role="button" aria-label="Previous members">
        <img class="img" alt="Previous members" src="/img/ep-arrow-right-bold.svg" />
      </div>
      <div class="autres-membres-list">
        <div
          v-for="autreMembre in paginatedAutresMembres"
          :key="autreMembre.Numero"
          class="card-list"
          @click="goToMembreDetail(autreMembre.Numero)"
          role="button"
          :aria-label="`View details for ${autreMembre['Nom']}`"
          tabindex="0"
        >
          <template v-if="getAutreMembreLogoInfo(autreMembre).imageUrl && !autresMembresLogosErrors[autreMembre.Numero]">
            <img
              class="img-list"
              :alt="autreMembre['Nom'] + ' Logo'"
              :src="getAutreMembreLogoInfo(autreMembre).imageUrl"
              loading="lazy"
              decoding="async"
              fetchpriority="low"
              @error="handleAutreMembreLogoError(autreMembre.Numero)"
            />
          </template>
          <template v-else-if="getAutreMembreLogoInfo(autreMembre).textForFallback">
            <div class="logo-nom">
              {{ getAutreMembreLogoInfo(autreMembre).textForFallback }}
            </div>
          </template>
        </div>
        <div v-if="!paginatedAutresMembres.length && filteredAutresMembres.length > 0" class="no-autres-membres">
            <p>No other members to display.</p>
        </div>
        <div v-if="!filteredAutresMembres.length" class="no-autres-membres">
            <p>No other members available.</p>
        </div>
      </div>
      <div class="handle-bottom" v-if="!isMobile" @click="nextAutresMembresPage" role="button" aria-label="Next members">
        <img
          class="img"
          alt="Next members"
          src="/img/ep-arrow-right-bold-1.svg"
        />
      </div>
    </div>

    <!-- Image Showcase Modal -->
    <div v-if="showcasedImage" class="image-showcase-overlay" @click="closeShowcase">
      <div class="image-showcase-content" @click.stop>
        <img :src="showcasedImage" :alt="showcasedImageName" class="showcased-image">
        <p class="showcased-image-name">{{ showcasedImageName }}</p>
        <button @click="closeShowcase" class="close-showcase-button">&times;</button>
      </div>
    </div>
  </div>
</template>

<script>
import ChipsCategory from "@/components/ChipsCategory.vue"; // Import ChipsCategory
import productCategoriesData from "@/product-categories.json";
import memberDetailsData from "@/member24v.json"; // Import for descriptions
import baseMembresData from "@/membres.json"; // Base members list
import member24vData from "@/member24v.json"; // Detailed members for enrichment
import productsData from "@/products-zones.json";

export default {
  name: "CardMembreExpanded",
  components: { // Register ChipsCategory
    ChipsCategory,
  },
  props: {
    membre: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      logoImageError: false,
      galleryImageErrors: [], // Dynamic array to handle variable number of images
      autresMembresLogosErrors: {}, // To track errors for "autres membres" logos { numero: true }
  allMembresBase: baseMembresData,
      autresMembresCurrentPage: 0,
      autresMembresItemsPerPage: 5, // Show 5 other members at a time
      showcasedImage: null, // For image showcase
      isMobile: false,
    };
  },
  computed: {
    matchedCategories() {
      if (!this.membre || !this.membre.Numero) {
        return [];
      }

      const memberProductData = productsData.find(p => String(p.Numero) === String(this.membre.Numero));
      if (!memberProductData || !memberProductData['Produits BIO']) {
        return [];
      }

      const memberProducts = memberProductData['Produits BIO'].map(p => p.toLowerCase());
      const matched = new Set();

      for (const category in productCategoriesData) {
        const keywords = productCategoriesData[category].map(k => k.toLowerCase());
        if (memberProducts.some(product => keywords.some(keyword => product.includes(keyword)))) {
          matched.add(category);
        }
      }
      
      return Array.from(matched);
    },
    detailEntry() {
      if (!this.membre || !this.membre.Numero) return null;
      return memberDetailsData.find(item => String(item.Numero) === String(this.membre.Numero)) || null;
    },
    displayName() {
      if (this.detailEntry && this.detailEntry.Nom && this.detailEntry.Nom.trim() !== '') return this.detailEntry.Nom;
      if (this.membre && this.membre['Nom'] && this.membre['Nom'].trim() !== '') return this.membre['Nom'];
      if (this.membre && this.membre['Nom de la société'] && this.membre['Nom de la société'].trim() !== '') return this.membre['Nom de la société'];
      return 'Nom non disponible';
    },
    phoneValue() {
      return (this.detailEntry && this.detailEntry['Téléphone']) || this.membre['Numéro téléphone'] || '';
    },
    addressValue() {
      return (this.detailEntry && (this.detailEntry['Adresse'] || this.detailEntry['Adresse physique'])) || this.membre['Adresse physique'] || '';
    },
    emailValue() {
      return (this.detailEntry && (this.detailEntry['Mail'] || this.detailEntry['Adresse e-mail'])) || this.membre['Adresse e-mail'] || '';
    },
    siteValue() {
      return (this.detailEntry && (this.detailEntry['Site web'] || this.detailEntry['Site Web'])) || this.membre['Site Web'] || '';
    },
    logoInfo() {
      if (!this.membre) {
        return { imageUrl: null, textForFallback: 'N/A' };
      }
      let imageUrl = null;
      if (this.membre.Numero) {
        // Assuming logos are in /public/logo/ and named as [Numero].png
        // Vue CLI projects serve /public directory at the root.
        imageUrl = `/logo/${this.membre.Numero}.webp`;
      }
      return {
        imageUrl: imageUrl,
        textForFallback: this.displayName
      };
    },
    memberName() { return this.displayName; },
    memberDescription() {
      if (!this.membre || !this.membre.Numero) {
        return "Déscription non disponible";
      }
      const detailEntry = memberDetailsData.find(
        (item) => String(item.Numero) === String(this.membre.Numero)
      );
      return detailEntry && detailEntry["Description FR"] 
        ? detailEntry["Description FR"] 
        : "Déscription non disponible";
    },
    galleryImages() {
      if (!this.membre || !this.membre.Numero) {
        return [];
      }
      const numero = this.membre.Numero;
      const images = [];
      
      // Find product data for this member to get product images count
      const memberProductData = productsData.find(p => String(p.Numero) === String(numero));
      const productImagesCount = memberProductData && memberProductData['Images Produits'] 
        ? memberProductData['Images Produits'].length 
        : 0;
      
      // Calculate how many numbered images to generate: 4 - productImagesCount
      const numberedImagesCount = Math.max(0, 4 - productImagesCount);
      
      // Add numbered images (1.jpg, 2.jpg, etc.) based on calculation
      for (let i = 1; i <= numberedImagesCount; i++) {
        // Check if the image has errored before adding it
        if (!this.galleryImageErrors[i-1]) {
          images.push(`/produits/${numero}/${i}.webp`);
        }
      }
      
      // Add product images from products-zones.json
      if (memberProductData && memberProductData['Images Produits']) {
        memberProductData['Images Produits'].forEach((imageName, index) => {
          const errorIndex = numberedImagesCount + index; // Offset by numbered images count
          // Check if the image has errored before adding it
          if (!this.galleryImageErrors[errorIndex]) {
            images.push(`/produits/${numero}/${imageName}`);
          }
        });
      }
      
      // Filter out errored images from the final list to display
      // This ensures that if an image path is generated but later errors, it's not shown
      // However, the primary filtering happens by not pushing errored images above.
      // This computed property will re-evaluate if necessary, though direct modification should trigger it
      return images.filter((_, index) => !this.galleryImageErrors[index]);
    },
    mergedAutresMembres() {
      // Merge base + detailed: prefer detailed fields (Nom, Téléphone, etc.) similar to MembersCards logic
      return this.allMembresBase.map(base => {
        const detail = member24vData.find(d => String(d.Numero) === String(base.Numero));
        const displayName = (detail && detail.Nom && detail.Nom.trim() !== '')
          ? detail.Nom
          : (base['Nom'] || base['Nom de la société'] || 'Nom non disponible');
        return {
          ...base,
          displayName,
          _detail: detail || null
        };
      });
    },
    filteredAutresMembres() {
      const allValid = this.mergedAutresMembres.filter(m => m && m.Numero);
      if (!this.membre || !this.membre.Numero) {
        return allValid.slice().sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'fr', { sensitivity: 'base' }));
      }
      const others = allValid.filter(m => String(m.Numero) !== String(this.membre.Numero));
      others.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'fr', { sensitivity: 'base' }));
      const currentName = (this.memberName || this.displayName || '').toString();
      let startIdx = others.findIndex(m => (m.displayName || '').localeCompare(currentName, 'fr', { sensitivity: 'base' }) > 0);
      if (startIdx === -1) startIdx = 0;
      const rotated = others.slice(startIdx).concat(others.slice(0, startIdx));
      return rotated;
    },
    totalAutresMembresPages() {
      if (!this.filteredAutresMembres || this.filteredAutresMembres.length === 0) {
        return 0;
      }
      const pageSize = this.isMobile ? 3 : this.autresMembresItemsPerPage;
      return Math.ceil(this.filteredAutresMembres.length / pageSize);
    },
    paginatedAutresMembres() {
      if (this.totalAutresMembresPages === 0) {
        return [];
      }
      const pageSize = this.isMobile ? 3 : this.autresMembresItemsPerPage;
      const start = this.autresMembresCurrentPage * pageSize;
      const end = start + pageSize;
      return this.filteredAutresMembres.slice(start, end);
    },
    showcasedImageName() {
      if (!this.showcasedImage) return '';
      const parts = this.showcasedImage.split('/');
      return parts[parts.length - 1];
    },
  },
  methods: {
    handleLogoError() {
      this.logoImageError = true;
    },
    handleGalleryImageError(index) {
      // Vue 3: Directly update the array element for reactivity
      this.galleryImageErrors[index] = true;
      // Force re-computation of galleryImages if necessary, though direct modification should trigger it
      // For Vue 2, you would need this.$set(this.galleryImageErrors, index, true);
    },
    getAutreMembreLogoInfo(autreMembre) {
      if (!autreMembre) {
        return { imageUrl: null, textForFallback: "N/A" };
      }
      const imageUrl = autreMembre.Numero ? `/logo/${autreMembre.Numero}.webp` : null;
      const textForFallback = autreMembre.displayName || autreMembre['Nom'] || autreMembre['Nom de la société'] || 'Logo';
      return { imageUrl, textForFallback };
    },
    handleAutreMembreLogoError(numero) {
      this.autresMembresLogosErrors = {
        ...this.autresMembresLogosErrors,
        [String(numero)]: true,
      };
    },
    nextAutresMembresPage() {
      if (this.totalAutresMembresPages === 0) return;
      this.autresMembresCurrentPage =
        (this.autresMembresCurrentPage + 1) % this.totalAutresMembresPages;
    },
    prevAutresMembresPage() {
      if (this.totalAutresMembresPages === 0) return;
      this.autresMembresCurrentPage =
        (this.autresMembresCurrentPage - 1 + this.totalAutresMembresPages) %
        this.totalAutresMembresPages;
    },
    goToMembreDetail(numero) {
      if (numero) {
        // Check if already on the same member's page to avoid redundant navigation
        if (this.membre && String(this.membre.Numero) === String(numero)) {
          return;
        }
        this.$router.push({
          name: "PageDetailsMembre", // Use the name from the router configuration
          params: { id: String(numero) }, 
        });
      }
    },
    showcaseImage(imageSrc) {
      this.showcasedImage = imageSrc;
    },
    closeShowcase() {
      this.showcasedImage = null;
    },
    callPhone(phoneNumber) {
      if (phoneNumber) {
        // Clean the phone number by removing spaces and special characters except + and numbers
        const cleanedNumber = phoneNumber.replace(/[^\d+]/g, '');
        window.location.href = `tel:${cleanedNumber}`;
      }
    },
    sendEmail(email) {
      if (email) {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(email)) {
          window.location.href = `mailto:${email}`;
        }
      }
    },
    openWebsite(url) {
      if (url) {
        // Ensure URL has protocol
        let finalUrl = url;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          finalUrl = `https://${url}`;
        }
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
      }
    },
    updateIsMobile() {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 1024;
      if (wasMobile !== this.isMobile) {
        // Reset page if needed when layout/page size changes
        if (this.autresMembresCurrentPage >= this.totalAutresMembresPages) {
          this.autresMembresCurrentPage = 0;
        }
      }
    },
  },
  mounted() {
    this.updateIsMobile();
    window.addEventListener('resize', this.updateIsMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateIsMobile);
  },
  watch: {
    membre: {
      handler(newMembre, oldMembre) {
        this.logoImageError = false;
        
        // Initialize galleryImageErrors array with correct size
        if (newMembre && newMembre.Numero) {
          // Find product data for this member
          const memberProductData = productsData.find(p => String(p.Numero) === String(newMembre.Numero));
          const productImagesCount = memberProductData && memberProductData['Images Produits'] 
            ? memberProductData['Images Produits'].length 
            : 0;
          
          // Total images = 4 numbered images + product images from JSON
          const totalImages = 4 + productImagesCount;
          this.galleryImageErrors = new Array(totalImages).fill(false);
        } else {
          // Default fallback for 4 numbered images
          this.galleryImageErrors = [false, false, false, false];
        }

        const isInitialLoad = !oldMembre && newMembre;
        const hasMemberChanged = newMembre && oldMembre && String(newMembre.Numero) !== String(oldMembre.Numero);

        if (isInitialLoad || hasMemberChanged) {
          this.autresMembresCurrentPage = 0; 
        }
      },
      deep: true,
      immediate: true,
    },
    autresMembresCurrentPage: {
        handler() {
            this.autresMembresLogosErrors = {};
        },
        // immediate: true // Not needed here, as initial reset is handled by 'membre' watcher
    }
  }
};
</script>

<style>
.card-membre-expanded {
  display: none;
  align-items: flex-start;
  display: inline-flex;
  gap: 12px;
      width: -webkit-fill-available;
  /* left: 267px; */
  /* position: absolute; */
  /* top: 404px; */
  margin: 12px 8vw 64px 8vw;

}

.card-membre-expanded .main-card {
  background-color: #e6e3b5;
  border-radius: 48px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 80%;
  height: max-content;
  min-height: 580px; /* Set a minimum height for consistency */
  padding: 48px 4vw;
  gap: 0px 24px;
  /* height: 588px; */
  /* position: relative; */
  /* width: 936px; */
}

.card-membre-expanded .card-logo {
  align-items: center;
  border-radius: 100px;
  display: flex;
  gap: 10px;
  /* height: 144px; */
  justify-content: center;
  /* left: 302px; */
  /* position: absolute; */
  /* top: 410px; */
  width: max-content;
  margin-bottom: 24px;
}

.card-membre-expanded .aplv-out {
  height: 120px;
  width: 140px;
  object-fit: contain; /* Use contain to prevent cropping */
  position: relative;
}
.card-membre-expanded .card-title {
  color: #000000;
  font-family: "Bw Modelica SS02", "Mona-Sans-Bold", Helvetica;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  margin-bottom: 24px;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.1);
  /* position: absolute; */
  /* top: 0; */
}

.card-membre-expanded .card-title.with-logo {
  font-size: 20px;
}

.card-membre-expanded .about-membre-text-wrapper {
  display: flex;
  flex-direction: column;
  /* justify-content: space-between; */ /* Removed to stop pushing content to edges */
}

.card-membre-expanded .categories-wrapper {
  align-items: flex-start;
  display: flex; /* Use flex for better alignment and wrapping */
  flex-wrap: wrap;
  gap: 8px; /* Adjusted gap for better spacing */
  margin-bottom: 24px; /* Retain bottom margin */
}

.card-membre-expanded .text-logo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 140px; /* Match new logo width */
  height: 120px; /* Match new logo height */
  color: #3b723e; /* Match theme color */
  font-size: 24px; /* Increased font size for name */
  font-weight: bold;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
  overflow: hidden;
  line-height: 1.2;
  word-break: break-word; /* Break long names if necessary */
  background-color: #d1cfab; /* A more solid background */
  border: 2px solid #b5b39c; /* Solid border for a deliberate look */
  border-radius: 16px; /* Rounded corners */
}

/* Remove or update previous .text-logo-display if it exists and is no longer needed or conflicts */
/*.text-logo-display {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 106px; 
  height: 90px; 
  background-color: #D1D5DB; 
  color: #1F2937; 
  font-size: 18px; 
  font-weight: bold;
  border-radius: 8px; 
  text-align: center;
  padding: 8px;
  box-sizing: border-box; 
  overflow: hidden;
  line-height: 1.2; 
  word-break: break-word; 
}*/
.card-membre-expanded .card-description {
  color: #000000;
  font-family: "Mona-Sans-Light", Helvetica;
  font-size: 14px; /* Increased font size slightly */
  font-weight: 300;
  /* left: 0; */
  letter-spacing: 0;
  margin-bottom: 0; /* Removed margin to let flexbox handle spacing */
  margin-top: 24px; /* Add some space above the description */
  flex-grow: 1; /* Allow this element to grow and fill space */
  line-height: 22px; /* Increased line height */
  text-align: center; /* Center the description text */
  max-height: 250px; /* Set a max height for the description */
  overflow-y: auto; /* Add scroll for overflow */
  padding-right: 5px; /* Add padding for the scrollbar */
  /* position: absolute; */
  /* top: 141px; */
  /* width: 367px; */
}
.card-membre-expanded .contact-gallery-wrapper{
  margin-top: 116px;
}
.card-membre-expanded .contact {
  align-items: flex-start;
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex-wrap: wrap;
  gap: 12px 24px;
  justify-content: space-between;
  /* left: 396px; */
  /* position: absolute; */
  /* top: 4px; */
  /* width: 393px; */
}

.card-membre-expanded .contact-item {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  gap: 8px;
  position: relative;
}

.card-membre-expanded .img {
  height: 24px;
  /* position: relative; */
  width: 24px;
}

.card-membre-expanded .clickable-icon {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  padding: 2px;
}

.card-membre-expanded .clickable-icon:hover {
  transform: scale(1.1);
  background-color: rgba(59, 114, 62, 0.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.card-membre-expanded .clickable-contact {
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  padding: 4px 8px;
  margin: -4px -8px;
}

.card-membre-expanded .clickable-contact:hover {
  transform: translateY(-1px);
  background-color: rgba(59, 114, 62, 0.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

.card-membre-expanded .clickable-contact:hover .img {
  transform: scale(1.1);
}

.card-membre-expanded .clickable-contact:hover .contact-text {
  color: #3b723e;
}

.card-membre-expanded .contact-text {
  color: #000000;
  font-family: "Mona-Sans-Light", Helvetica;
  font-size: 13px;
  font-weight: 300;
  letter-spacing: 0;
  line-height: 18px;
  margin-top: -1.00px;
  position: relative;
  width: fit-content;
}

.card-membre-expanded .card-gallery {
  margin-top: 24px;
  align-items: flex-start;
  display: flex;
  flex-wrap: wrap;
  gap: 8px 8px;
  min-height: 100px; /* Ensure the container has some height for the message */
  /* height: 260px; */ /* Commented out to allow dynamic height or fit message */
  overflow: hidden;
  overflow-y: auto; /* Keep scroll for many images, though we expect max 4 */
  /* position: relative; */ /* Ensure no absolute positioning issues */
  /* width: 436px; */ /* Commented out for flexibility */
}

.card-membre-expanded .frame::-webkit-scrollbar {
  display: none;
  width: 0;
}

.card-membre-expanded .gallery-img {
   height: 150px; 
  object-fit: cover;
  position: relative;
  width: 48%;
  cursor: pointer; /* Make images appear clickable */
}

.card-membre-expanded .img-paginate-wrapper {
  width: 100%;
  display: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
}
.card-membre-expanded .img-paginate {
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
}
.card-membre-expanded .img-paginate-item.active{
  background-color: #3b723e;
}
  .card-membre-expanded .img-paginate-item {
  background-color: #3b723e53;
  border-radius: 6px;
  height: 6px;
  width: 7px;
}

.card-membre-expanded .autres-membres {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  background-color: #e6e3b566;
  border-radius: 8px;
  position: relative;
  width: 20%;
  padding: 10px 0; /* Added padding for spacing of handles */
}

.card-membre-expanded .handle-top {
  align-items: center;
  border-radius: 100px;
  box-shadow: 0px 4px 4px #00000040;
  display: flex;
  gap: 10px;
  height: 48px;
  justify-content: center;
  /* left: 126px; */ /* Removed for flex centering */
  padding: 10px;
  /* position: absolute; */ /* Removed for flex flow */
  /* top: -12px; */ /* Adjusted by parent padding */
  width: 48px;
  cursor: pointer;
  margin-bottom: 5px; /* Space between handle and list */
}


.card-membre-expanded .handle-bottom {
  box-shadow: 0px 4px 4px #00000040;
  align-items: center;
  border-radius: 100px;
  display: flex;
  gap: 10px;
  height: 48px;
  justify-content: center;
  /* left: 126px; */ /* Removed for flex centering */
  padding: 10px 10px 2px 10px;
  /* position: absolute; */ /* Removed for flex flow */
  /* top: 552px; */ /* Adjusted by parent padding and flex flow */
  width: 48px;
  cursor: pointer;
  margin-top: 5px; /* Space between list and handle */
}
 

.card-membre-expanded .autres-membres-list {
  display: flex;
  flex-direction: column;
  width: 90%; /* Take most of the parent's width */
  margin: 0 auto; /* Center the list */
  /* height: 512px; */ /* Allow dynamic height based on content */
  overflow: hidden; /* Hide any overflow if items are too many, though pagination should prevent this */
}
.card-membre-expanded .card-list {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 6px 8px #0000002a;
    display: flex;
  flex-direction: column;
  height: 110px;
  justify-content: center;
  margin-top: -12px;
  width: 100%; /* Fill the width of .autres-membres-list */
  cursor: pointer;
  transition: transform 0.2s ease-in-out; /* Smooth hover effect */
}
.card-membre-expanded .card-list:first-child {
  margin-top: 0; /* Remove negative margin for the first item */
}
.card-membre-expanded .card-list:hover {
  transform: translateY(-2px); /* Slight lift on hover */
  box-shadow:  0 0 6px 8px #0000002a;
}

.card-membre-expanded .logo-nom {
  color: #3b723e; /* Match theme color */
  font-family: "Mona-Sans-Bold", Helvetica;
  font-size: 1.1em; /* Slightly larger font */
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.2;
  position: relative;
  white-space: normal;
  text-align: center;
  width: 90%;
  padding: 8px; /* Increased padding */
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex; /* Use flexbox for centering */
  align-items: center;
  justify-content: center;
  height: 100%; /* Fill the container */
  box-sizing: border-box;
  /* -webkit-line-clamp: 2; */ /* Flexbox centering is better for this */
  /* line-clamp: 2; */
  /* -webkit-box-orient: vertical; */
}
.card-membre-expanded .img-list {
  padding: 4px; /* Reduced padding */
  mix-blend-mode: multiply;
  object-fit: contain; /* Changed to contain to ensure logo visibility */
  max-height: 56px; /* Max height to fit within card-list */
  width: auto; /* Adjust width automatically */
  max-width: 90%; /* Ensure it doesn't overflow */
  overflow: hidden;
}
/* Remove old static z-index styles */
/*.card-membre-expanded .card-1 {
  z-index: 9;
}
.card-membre-expanded .card-2 {
  z-index: 8;
}
.card-membre-expanded .card-3 {
  z-index: 7;
}
.card-membre-expanded .card-4 {
  z-index: 6;
}
.card-membre-expanded .card-5 {
  z-index: 5;
}
.card-membre-expanded .card-6 {
  z-index: 4;
}
.card-membre-expanded .card-7 {
  z-index: 3;
}
.card-membre-expanded .card-8 {
  z-index: 2;
}
.card-membre-expanded .card-9 {
  z-index: 1;
}*/

.no-autres-membres {
  text-align: center;
  padding: 10px;
  font-style: italic;
  color: #555;
  height: 64px; /* Match card height for consistency */
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-images-text {
  width: 100%;
  height: 308px; /* Increased height to better fill the gallery space */
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b723e;
  background-color: #e0dcc3;
  border: 2px dashed #c5c69c;
  border-radius: 16px;
  font-style: italic;
  font-size: 14px;
}

.placeholder-text {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280; /* Muted text color for placeholder */
  background-color: transparent; /* Remove background */
  border: none; /* Remove border */
  font-style: italic;
  padding: 20px;
}

.image-showcase-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.image-showcase-content {
  position: relative;
  background-color: #fff;
  padding: 20px;
  border-radius: 16px;
  text-align: center;
  max-width: 90vw;
  max-height: 90vh;
}

.showcased-image {
  max-width: 100%;
  max-height: calc(80vh - 50px); /* Adjust based on content box padding and text height */
  display: block;
  margin: 0 auto;
  border-radius: 8px;
}

.showcased-image-name {
  margin-top: 10px;
  font-size: 16px;
  color: #333;
  font-family: "Mona-Sans-Light", Helvetica;
}

.close-showcase-button {
  position: absolute;
  top: 10px;
  right: 15px;
  background: none;
  border: none;
  font-size: 30px;
  color: #333;
  cursor: pointer;
}

/* Tablet and Mobile Responsive Styles */
@media (max-width: 1024px) {
  /* Layout: stack vertically and use full width */
  .card-membre-expanded {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    margin: 12px 4vw 32px 4vw; /* tighter outer spacing */
  }
  .card-membre-expanded .main-card {
    grid-template-columns: 1fr; /* single column */
    width: 100%;
    min-height: auto; /* let content define height */
    padding: 24px 4vw; /* slightly tighter padding */
  }
  .card-membre-expanded .contact-gallery-wrapper {
    margin-top: 24px; /* reduce from large desktop spacing */
  }

  /* Autres membres: full-width and compact */
  .card-membre-expanded .autres-membres {
    width: 100%;
    flex-direction: column;
    height: auto;
    padding: 12px;
    background-color: #e6e3b540; /* lighter background */
  }
  .card-membre-expanded .autres-membres-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 8px;
  }
  .card-membre-expanded .autres-membres-header .autres-title {
    color: #3b723e;
    font-family: "Mona-Sans-Bold", Helvetica;
    font-size: 14px;
  }
  .card-membre-expanded .arrow-btn {
    border: none;
    background: #e6e3b5;
    border-radius: 9999px;
    width: 36px;
    height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    cursor: pointer;
  }
  .card-membre-expanded .arrow-btn .img {
    width: 20px;
    height: 20px;
  }
  .card-membre-expanded .autres-membres-list {
    flex-direction: column;
    width: 100%;
    gap: 8px;
    margin: 0;
  }
  .card-membre-expanded .card-list {
    min-width: 0;
    width: 100%;
    height: 56px;
    margin-top: 0;
    padding: 6px 10px;
    flex-direction: row;
    gap: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.12);
  }
  .card-membre-expanded .img-list {
    max-height: 36px;
    max-width: 36px;
  }
  .card-membre-expanded .logo-nom {
    font-size: 0.95em;
    padding: 0;
    text-align: left;
    width: auto;
    justify-content: flex-start;
    align-items: center;
  }

  /* Hide large handles on mobile (header arrows are used) */
  .card-membre-expanded .handle-top,
  .card-membre-expanded .handle-bottom {
    display: none;
  }
}

@media (max-width: 480px) {
  .card-membre-expanded .autres-membres {
    padding: 8px;
  }
  .card-membre-expanded .arrow-btn {
    width: 32px;
    height: 32px;
  }
  .card-membre-expanded .arrow-btn .img {
    width: 18px;
    height: 18px;
  }
}
/*.card-membre-expanded .frame-wrapper {
  
}

.card-membre-expanded .image-collee-wrapper {
  flex: 1;
  flex-grow: 1;
  position: relative;
  width: 234px;
}

.card-membre-expanded .image-collee {
  height: 56px;
  left: 63px;
  mix-blend-mode: multiply;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 110px;
}

.card-membre-expanded .card-2 {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 4px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 392px;
  width: 300px;
}

.card-membre-expanded .image-wrapper {
  height: 44px;
  margin-bottom: -6.00px;
  margin-top: -6.00px;
  position: relative;
  width: 209px;
}

.card-membre-expanded .image {
  height: 44px;
  left: 68px;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 73px;
}

.card-membre-expanded .card-3 {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 4px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 336px;
  width: 300px;
}

.card-membre-expanded .frame-4 {
  background-image: url(../../../../public/img/frame-36.png);
  background-position: 50% 50%;
  background-size: cover;
  height: 57px;
  margin-bottom: -12.50px;
  margin-top: -12.50px;
  position: relative;
  width: 170px;
}

.card-membre-expanded .card-4 {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 4px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 280px;
  width: 300px;
}

.card-membre-expanded .logo-nom {
  color: #000000;
  font-family: "Mona-Sans-Bold", Helvetica;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 24px;
  position: relative;
  white-space: nowrap;
  width: fit-content;
}

.card-membre-expanded .bionexx-wrapper {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 4px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 224px;
  width: 300px;
}

.card-membre-expanded .bionexx {
  height: 28px;
  position: relative;
  width: 133px;
}

.card-membre-expanded .card-5 {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 4px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 168px;
  width: 300px;
}

.card-membre-expanded .image-2 {
  height: 28px;
  object-fit: cover;
  position: relative;
  width: 198px;
}

.card-membre-expanded .bioesoil-wrapper {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 4px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 112px;
  width: 300px;
}

.card-membre-expanded .bioesoil {
  height: 28.5px;
  position: relative;
  width: 114px;
}

.card-membre-expanded .longo-iaby-wrapper {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 4px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 56px;
  width: 300px;
}

.card-membre-expanded .longo-iaby {
  align-self: stretch;
  height: 46px;
  margin-bottom: -7.00px;
  margin-top: -7.00px;
  position: relative;
  width: 100%;
}

.card-membre-expanded .card-6 {
  align-items: center;
  background-color: #e6e3b5;
  border-radius: 8px;
  box-shadow: 0px 4px 0px #0000001a;
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 64px;
  justify-content: center;
  left: 0;
  padding: 16px;
  position: absolute;
  top: 0;
  width: 300px;
}
.no-images-text {
  width: 100%;
  height: 308px; 
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b723e;
  background-color: #e0dcc3;
  border: 2px dashed #c5c69c;
  border-radius: 16px;
  font-style: italic;
  font-size: 14px;
} */
</style>